import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { compensationService } from '@/services/compensation/compensationService';
import { compensationKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import type { CompensationRule } from '@/services/compensation/types';

/**
 * Hook for managing compensation rule assignments
 * Provides queries and mutations for the new assignment-based system
 */
export const useCompensationAssignments = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /**
   * Get rules assigned to a specific employee
   */
  const useEmployeeAssignedRules = (
    profileId: string, 
    options: { ruleType?: CompensationRule['rule_type']; activeOnly?: boolean } = {}
  ) => {
    return useQuery({
      queryKey: [...compensationKeys.all, 'assigned-rules', profileId, options],
      queryFn: () => compensationService.getAssignedRules(profileId, options),
      enabled: !!profileId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  /**
   * Get all employees assigned to a specific rule
   */
  const useRuleAssignments = (ruleId: string) => {
    return useQuery({
      queryKey: [...compensationKeys.all, 'rule-assignments', ruleId],
      queryFn: () => compensationService.getRuleAssignments(ruleId),
      enabled: !!ruleId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });
  };

  /**
   * Enhanced employee rules query that uses the new assignment model
   */
  const useEmployeeCompensationRulesEnhanced = (
    profileId: string,
    options: { ruleType?: CompensationRule['rule_type']; activeOnly?: boolean; effectiveDate?: Date } = {}
  ) => {
    return useQuery({
      queryKey: [...compensationKeys.all, 'enhanced-rules', profileId, options],
      queryFn: () => compensationService.getEmployeeCompensationRulesEnhanced({
        profileId,
        ruleType: options.ruleType,
        activeOnly: options.activeOnly,
        effectiveDate: options.effectiveDate
      }),
      enabled: !!profileId,
      staleTime: 3 * 60 * 1000,
      gcTime: 8 * 60 * 1000,
    });
  };

  /**
   * Validate rule assignment before executing
   */
  const useValidateRuleAssignment = () => {
    return useMutation({
      mutationFn: async ({ ruleId, profileIds }: { ruleId: string; profileIds: string[] }) => {
        return await compensationService.validateRuleAssignment(ruleId, profileIds);
      },
    });
  };

  /**
   * Assign rule to multiple employees with validation
   */
  const useAssignRule = () => {
    return useMutation({
      mutationFn: async ({ 
        ruleId, 
        profileIds, 
        assignedBy,
        skipValidation = false 
      }: { 
        ruleId: string; 
        profileIds: string[]; 
        assignedBy?: string;
        skipValidation?: boolean;
      }) => {
        // Pre-flight validation unless skipped
        if (!skipValidation) {
          const validation = await compensationService.validateRuleAssignment(ruleId, profileIds);
          if (!validation.valid) {
            throw new Error(`Assignment validation failed: ${validation.errors.join(', ')}`);
          }
        }

        return await compensationService.assignRuleToEmployees(ruleId, profileIds, assignedBy);
      },
      onMutate: async ({ ruleId, profileIds }) => {
        // Cancel outgoing queries to prevent race conditions
        await queryClient.cancelQueries({ queryKey: [...compensationKeys.all, 'assigned-rules'] });
        await queryClient.cancelQueries({ queryKey: [...compensationKeys.all, 'rule-assignments', ruleId] });

        // Snapshot previous values for rollback
        const previousAssignments = queryClient.getQueryData([...compensationKeys.all, 'rule-assignments', ruleId]);
        const previousEmployeeRules = profileIds.map(profileId => ({
          profileId,
          data: queryClient.getQueryData([...compensationKeys.all, 'assigned-rules', profileId])
        }));

        return { previousAssignments, previousEmployeeRules };
      },
      onSuccess: (data, variables) => {
        // Invalidate and refetch related queries
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'rule-assignments', variables.ruleId] });
        
        variables.profileIds.forEach(profileId => {
          queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assigned-rules', profileId] });
          queryClient.invalidateQueries({ queryKey: compensationKeys.rule(profileId) });
          queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assignment-history', profileId] });
        });

        queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
        queryClient.invalidateQueries({ queryKey: ['compensation', 'company-assignment-history'] });

        toast({
          title: "Assignment Successful",
          description: `Rule assigned to ${variables.profileIds.length} employee(s).`,
          duration: 5000,
        });
      },
      onError: (error: any, variables, context) => {
        // Rollback optimistic updates
        if (context?.previousAssignments) {
          queryClient.setQueryData(
            [...compensationKeys.all, 'rule-assignments', variables.ruleId],
            context.previousAssignments
          );
        }

        context?.previousEmployeeRules.forEach(({ profileId, data }) => {
          if (data) {
            queryClient.setQueryData([...compensationKeys.all, 'assigned-rules', profileId], data);
          }
        });

        toast({
          title: "Assignment Failed",
          description: error.message || "Failed to assign rule to employees.",
          variant: "destructive",
          duration: 8000,
        });
      },
    });
  };

  /**
   * Unassign rule from employee
   */
  const useUnassignRule = () => {
    return useMutation({
      mutationFn: async ({ ruleId, profileId }: { ruleId: string; profileId: string }) => {
        return await compensationService.unassignRule(ruleId, profileId);
      },
      onMutate: async ({ ruleId, profileId }) => {
        // Cancel queries and snapshot for rollback
        await queryClient.cancelQueries({ queryKey: [...compensationKeys.all, 'assigned-rules', profileId] });
        await queryClient.cancelQueries({ queryKey: [...compensationKeys.all, 'rule-assignments', ruleId] });

        const previousEmployeeRules = queryClient.getQueryData([...compensationKeys.all, 'assigned-rules', profileId]);
        const previousAssignments = queryClient.getQueryData([...compensationKeys.all, 'rule-assignments', ruleId]);

        // Optimistically update cache
        queryClient.setQueryData([...compensationKeys.all, 'assigned-rules', profileId], (old: any) =>
          old ? old.filter((rule: any) => rule.id !== ruleId) : []
        );

        return { previousEmployeeRules, previousAssignments };
      },
      onSuccess: (_, variables) => {
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assigned-rules', variables.profileId] });
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'rule-assignments', variables.ruleId] });
        queryClient.invalidateQueries({ queryKey: compensationKeys.rule(variables.profileId) });
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assignment-history', variables.profileId] });
        queryClient.invalidateQueries({ queryKey: ['compensation', 'company-assignment-history'] });

        toast({
          title: "Unassignment Successful",
          description: "Rule unassigned successfully.",
          duration: 5000,
        });
      },
      onError: (error: any, variables, context) => {
        // Rollback optimistic updates
        if (context?.previousEmployeeRules) {
          queryClient.setQueryData(
            [...compensationKeys.all, 'assigned-rules', variables.profileId],
            context.previousEmployeeRules
          );
        }
        if (context?.previousAssignments) {
          queryClient.setQueryData(
            [...compensationKeys.all, 'rule-assignments', variables.ruleId],
            context.previousAssignments
          );
        }

        toast({
          title: "Unassignment Failed",
          description: error.message || "Failed to unassign rule.",
          variant: "destructive",
          duration: 8000,
        });
      },
    });
  };

  /**
   * Bulk assign rules to multiple employees
   */
  const useBulkAssignRules = () => {
    return useMutation({
      mutationFn: async ({ 
        assignments 
      }: { 
        assignments: Array<{ ruleId: string; profileIds: string[]; assignedBy?: string }> 
      }) => {
        const results = await Promise.allSettled(
          assignments.map(assignment => 
            compensationService.assignRuleToEmployees(
              assignment.ruleId, 
              assignment.profileIds, 
              assignment.assignedBy
            )
          )
        );

        const successful = results
          .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
          .map(result => result.value);
        
        const failed = results
          .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
          .map(result => result.reason);

        return { successful, failed };
      },
      onSuccess: ({ successful, failed }, variables) => {
        // Invalidate all relevant queries
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assigned-rules'] });
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'rule-assignments'] });
        queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
        queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assignment-history'] });
        queryClient.invalidateQueries({ queryKey: ['compensation', 'company-assignment-history'] });

        const successCount = successful.length;
        const failCount = failed.length;

        toast({
          title: "Bulk Assignment Completed",
          description: `${successCount} assignments successful${failCount > 0 ? `, ${failCount} failed` : ''}.`,
          variant: failCount > 0 ? "destructive" : "default",
          duration: 8000,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Bulk Assignment Failed",
          description: "Failed to complete bulk assignments. Please try again.",
          variant: "destructive",
          duration: 8000,
        });
      },
    });
  };

  return {
    // Query hooks
    useEmployeeAssignedRules,
    useRuleAssignments,
    useEmployeeCompensationRulesEnhanced,
    
    // Mutation hooks
    useValidateRuleAssignment,
    useAssignRule,
    useUnassignRule,
    useBulkAssignRules,
    
    // Utility functions
    invalidateAssignmentQueries: () => {
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assigned-rules'] });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'rule-assignments'] });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'enhanced-rules'] });
    },
  };
};